import { getSupabase } from "./client";
import {
  normalizeContactImportRows,
  type ContactImportJson,
} from "./contactImportShared";

export { normalizeContactImportRows, type ContactImportJson };

export type ContactRow = {
  id: string;
  name: string;
  email: string;
  phone: string;
  roles: string;
  created_at?: string;
  updated_at?: string;
};

/** Escape `%` and `_` for Postgres ILIKE patterns. */
export function escapeIlikePattern(s: string): string {
  return s.replace(/\\/g, "\\\\").replace(/%/g, "\\%").replace(/_/g, "\\_");
}

/** First rows alphabetically by name (browse when search is empty). */
export async function listContactsAlphabetical(
  limit = 150
): Promise<{ data: ContactRow[] | null; error: string | null }> {
  const sb = getSupabase();
  if (!sb) return { data: null, error: "Supabase is not configured." };
  const { data, error } = await sb
    .from("contacts")
    .select("id,name,email,phone,roles,created_at,updated_at")
    .order("name", { ascending: true })
    .limit(limit);
  if (error) return { data: null, error: error.message };
  return { data: (data ?? []) as ContactRow[], error: null };
}

export async function searchContacts(
  query: string,
  limit = 20
): Promise<{ data: ContactRow[] | null; error: string | null }> {
  const sb = getSupabase();
  if (!sb) return { data: null, error: "Supabase is not configured." };
  const q = query.trim();
  if (!q) return { data: [], error: null };
  const pat = `%${escapeIlikePattern(q)}%`;
  const { data, error } = await sb
    .from("contacts")
    .select("id,name,email,phone,roles,created_at,updated_at")
    .ilike("name", pat)
    .order("name", { ascending: true })
    .limit(limit);
  if (error) return { data: null, error: error.message };
  return { data: (data ?? []) as ContactRow[], error: null };
}

/** Case-insensitive name match without wildcards (single row). */
export async function findContactByName(
  name: string
): Promise<{ data: ContactRow | null; error: string | null }> {
  const sb = getSupabase();
  if (!sb) return { data: null, error: "Supabase is not configured." };
  const n = name.trim();
  if (!n) return { data: null, error: null };
  const safe = escapeIlikePattern(n);
  const { data, error } = await sb
    .from("contacts")
    .select("id,name,email,phone,roles,created_at,updated_at")
    .ilike("name", safe)
    .limit(1)
    .maybeSingle();
  if (error) return { data: null, error: error.message };
  return { data: data as ContactRow | null, error: null };
}

export async function insertContact(
  row: Pick<ContactRow, "name"> & Partial<Pick<ContactRow, "email" | "phone" | "roles">>
): Promise<{ data: ContactRow | null; error: string | null }> {
  const sb = getSupabase();
  if (!sb) return { data: null, error: "Supabase is not configured." };
  const name = row.name.trim();
  if (!name) return { data: null, error: "Name is required." };
  const payload = {
    name,
    email: (row.email ?? "").trim(),
    phone: (row.phone ?? "").trim(),
    roles: (row.roles ?? "").trim(),
  };
  const { data, error } = await sb.from("contacts").insert(payload).select().single();
  if (error) return { data: null, error: error.message };
  return { data: data as ContactRow, error: null };
}

/** Insert if no row matches name (case-insensitive). */
export async function ensureContactByName(
  name: string,
  extras?: Partial<Pick<ContactRow, "email" | "phone" | "roles">>
): Promise<{ data: ContactRow | null; created: boolean; error: string | null }> {
  const { data: existing, error: findErr } = await findContactByName(name);
  if (findErr) return { data: null, created: false, error: findErr };
  if (existing) return { data: existing, created: false, error: null };
  const { data, error } = await insertContact({
    name: name.trim(),
    email: extras?.email,
    phone: extras?.phone,
    roles: extras?.roles,
  });
  if (error) return { data: null, created: false, error };
  return { data, created: true, error: null };
}

export async function bulkImportContacts(
  rows: ContactImportJson[]
): Promise<{ inserted: number; error: string | null }> {
  const sb = getSupabase();
  if (!sb) return { inserted: 0, error: "Supabase is not configured." };
  const payloads = normalizeContactImportRows(rows);
  if (payloads.length === 0) return { inserted: 0, error: null };
  const { error } = await sb.from("contacts").insert(payloads);
  if (error) return { inserted: 0, error: error.message };
  return { inserted: payloads.length, error: null };
}
